import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import styled from "styled-components";
import {
  FaCheckCircle,
  FaHome,
  FaFilePdf,
  FaEnvelope,
} from "react-icons/fa";
import { PDFDownloadLink } from "@react-pdf/renderer";
import confetti from "canvas-confetti";
import ReceiptPDF from "../ui/ReceiptPDF";
import { verifyPayment } from "../../api/services/paymentService";

/* -------------------------------------------------------
 * STYLES
 * ----------------------------------------------------- */
const PRIMARY_COLOR = "#182848";

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5f8ff, #e6f0ff);
  padding: 1rem;
`;

const ThankYouCard = styled.div`
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.14);
  padding: 2.5rem;
  max-width: 600px;
  width: 100%;
  text-align: center;
`;

const SuccessIcon = styled(FaCheckCircle)`
  font-size: 4rem;
  color: #28a745;
  margin-bottom: 1rem;
`;

const DetailList = styled.div`
  text-align: left;
  background: #f8f9fa;
  border-radius: 12px;
  padding: 1rem 1.25rem;
  margin: 1.5rem 0;
  font-size: 0.95rem;

  p {
    margin: 0.5rem 0;
  }

  strong {
    min-width: 120px;
    display: inline-block;
  }
`;

const Button = styled.button`
  background-color: ${PRIMARY_COLOR};
  color: white;
  border: none;
  border-radius: 10px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  cursor: pointer;

  &.outline {
    background: transparent;
    color: ${PRIMARY_COLOR};
    border: 2px solid ${PRIMARY_COLOR};
  }
`;

/* -------------------------------------------------------
 * COMPONENT
 * ----------------------------------------------------- */
const ThankYouPage = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("order_id");

  const [payment, setPayment] = useState(null);
  const [error, setError] = useState("");
  const confettiDone = useRef(false);

  /* ---------------------------------------------------
   * FETCH PAYMENT DETAILS
   * --------------------------------------------------- */
  useEffect(() => {
    if (!orderId) {
      setError("Missing order ID");
      return;
    }

    const fetchPayment = async () => {
      try {
        const res = await verifyPayment(orderId);

        if (res.status !== "SUCCESS") {
          setError("Payment not successful");
          return;
        }

        setPayment(res);
      } catch (err) {
        console.error(err);
        setError("Unable to load payment details");
      }
    };

    fetchPayment();
  }, [orderId]);

  /* ---------------------------------------------------
   * CONFETTI (ONCE)
   * --------------------------------------------------- */
  useEffect(() => {
    if (payment && !confettiDone.current) {
      confetti({
        particleCount: 120,
        spread: 70,
        origin: { y: 0.6 },
      });
      confettiDone.current = true;
    }
  }, [payment]);

  /* ---------------------------------------------------
   * ERROR STATE
   * --------------------------------------------------- */
  if (error) {
    return (
      <PageWrapper>
        <ThankYouCard>
          <FaCheckCircle size={48} color="#dc3545" />
          <h3 style={{ marginTop: "1rem" }}>We couldn’t confirm your payment</h3>

          <p style={{ color: "#555", marginTop: "0.75rem" }}>
            Don’t worry - your payment may still be processing or needs a quick check.
          </p>

          <p style={{ color: "#777", fontSize: "0.9rem" }}>
            If the amount was deducted, it will either be confirmed shortly or
            automatically refunded by the bank.
          </p>

          <div style={{ marginTop: "1.5rem" }}>
            <Link to="/" style={{ textDecoration: "none" }}>
              <Button>
                <FaHome /> Go to Home
              </Button>
            </Link>
          </div>

          <p style={{ marginTop: "1.25rem", fontSize: "0.85rem", color: "#666" }}>
            Need help? Contact us at{" "}
            <a href="mailto:info@q2d.in" style={{ color: PRIMARY_COLOR }}>
              info@q2d.in
            </a>
          </p>
        </ThankYouCard>
      </PageWrapper>
    );
  }

  if (!payment) return null;

  /* ---------------------------------------------------
   * SUCCESS UI
   * --------------------------------------------------- */
  return (
    <PageWrapper>
      <ThankYouCard>
        <SuccessIcon />
        <h2>Thank You!</h2>
        <p>Your payment was successful.</p>

        <DetailList>
          <p><strong>Order ID:</strong> {payment.orderId}</p>
          <p><strong>Amount:</strong> ₹{payment.amount}</p>
          <p>
            <strong>Paid At:</strong>{" "}
            {new Date(payment.paidAt).toLocaleString("en-IN")}
          </p>
        </DetailList>

        <Link to="/">
          <Button>
            <FaHome /> Home
          </Button>
        </Link>

        <PDFDownloadLink
          document={<ReceiptPDF payment={payment} />}
          fileName={`Receipt_${payment.orderId}.pdf`}
        >
          {({ loading }) => (
            <Button className="outline" style={{ marginLeft: "1rem" }}>
              <FaFilePdf /> {loading ? "Preparing…" : "Download Receipt"}
            </Button>
          )}
        </PDFDownloadLink>

        <p style={{ marginTop: "1rem" }}>
          <FaEnvelope />{" "}
          <a href={`mailto:info@q2d.in?subject=Payment Support - ${payment.orderId}`}>
            Contact Support
          </a>
        </p>
      </ThankYouCard>
    </PageWrapper>
  );
};

export default ThankYouPage;
